import {GraphQLError} from 'graphql';

import {
  useAvailableOrgQuery,
  useAvailableTeamQuery,
} from '../../../generated/graphql';
import {captureError} from '../../../integrations';
import {getErrMsgFromError} from '../../../util/errors';

type HandleMutationErrorProps = {
  analyticsLocation: string;
  err: any;
  errLabel: string;
  errExtra?: {[key: string]: any};
  trackingEventName: string;
  trackingOrgName: string | null;
  setErrorMsg: (m: string | null) => void;
};

export function handleMutationError({
  analyticsLocation,
  err,
  errLabel,
  errExtra,
  trackingEventName,
  trackingOrgName,
  setErrorMsg,
}: HandleMutationErrorProps) {
  captureError(err, errLabel, {extra: errExtra});
  const errMsg = getErrMsgFromError(err);
  setErrorMsg(
    `Error creating team${
      errMsg != null ? ` (${errMsg})` : ''
    }. Please try again.`
  );
  window.analytics?.track(trackingEventName, {
    organizationName: trackingOrgName,
    location: analyticsLocation,
    error: errMsg,
  });
}

type HandleGQLErrorsProps = {
  analyticsLocation: string;
  errors: GraphQLError[];
  errLabel: string;
  errExtra?: {[key: string]: any};
  trackingEventName: string;
  trackingOrgName: string | null;
  setErrorMsg: (m: string | null) => void;
};

export function handleGQLErrors({
  analyticsLocation,
  errors,
  errLabel,
  errExtra,
  trackingEventName,
  trackingOrgName,
  setErrorMsg,
}: HandleGQLErrorsProps) {
  const collatedErrMsg = errors.map(e => e.message).join(', ');
  captureError(collatedErrMsg, errLabel, {
    extra: errExtra,
  });
  setErrorMsg('Error creating team');
  window.analytics?.track(trackingEventName, {
    organizationName: trackingOrgName,
    location: analyticsLocation,
    error: collatedErrMsg,
  });
}

type UseAvailabilityParams = {
  entityName: string | null;
  orgName: string | null;
};

export function useAvailability({entityName, orgName}: UseAvailabilityParams): {
  loading: boolean;
  isEntityAvailable: boolean;
  isOrgAvailable: boolean;
} {
  const {loading: entityLoading, data: entityData} = useAvailableTeamQuery({
    variables: {
      teamName: entityName ?? '',
    },
    skip: !entityName,
  });
  const {loading: orgLoading, data: orgData} = useAvailableOrgQuery({
    variables: {
      name: orgName ?? '',
    },
    skip: !orgName,
    // https://github.com/apollographql/apollo-client/issues/11328 Fix for tests
    // which type quickly and cause a lack of re-renders when this query is complete
    // because the results are the same...
    notifyOnNetworkStatusChange: true,
  });

  return {
    loading: entityLoading || orgLoading,
    isEntityAvailable:
      !!entityName && !entityLoading && entityData?.entity?.available !== false,
    isOrgAvailable:
      !!orgName && !orgLoading && orgData?.organization?.available !== false,
  };
}
